import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Readable random password: letters + digits, no look-alikes (0/O, 1/l).
const generatePassword = () => {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(12));
  const pw = Array.from(bytes, (b) => chars[b % chars.length]).join('');
  return /\d/.test(pw) ? pw : `${pw.slice(0, 11)}7`;
};

// Trainer sets a client's password directly — no old password needed.
export const SetClientPasswordDialog = ({ client, open, onOpenChange }) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { email, password }

  // Fresh generated password each time the dialog opens (the parent opens it via `open`).
  useEffect(() => {
    if (open) { setPassword(generatePassword()); setError(''); setDone(null); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/clients/${client.id}/set-password`,
        { new_password: password }, { withCredentials: true });
      setDone({ email: res.data.email, password });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Nie udało się ustawić hasła');
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Skopiowano'));
  const field = 'bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl h-12 text-[var(--text-primary)]';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-soft)] text-[var(--text-primary)] max-w-md rounded-3xl">
        <DialogHeader><DialogTitle className="text-2xl font-extrabold tracking-tight">Ustaw hasło klienta</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-4" data-testid="client-password-done">
            <p className="text-sm text-[var(--text-muted)]">Hasło ustawione. Klient (albo Ty) loguje się tymi danymi:</p>
            {[['E-MAIL', done.email], ['HASŁO', done.password]].map(([l, v]) => (
              <div key={l}>
                <p className="label-text text-[var(--text-muted)] mb-1.5">{l}</p>
                <div className="flex items-center gap-2 glass-soft rounded-2xl px-4 py-3">
                  <span className="flex-1 text-sm font-mono select-all break-all">{v}</span>
                  <button onClick={() => copy(v)} aria-label={`Skopiuj ${l.toLowerCase()}`} className="text-[var(--text-muted)] hover:text-[#C9A84C]"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            <button onClick={() => onOpenChange(false)} className="cta-primary w-full h-12 rounded-full text-xs font-bold tracking-[0.2em] uppercase">Gotowe</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-[var(--text-muted)]">
              Nowe hasło dla <strong className="text-[var(--text-primary)]">{client.name}</strong>. Stare przestanie działać — nie musisz go znać.
            </p>
            {error && <p className="text-red-400 text-sm" data-testid="client-password-error">{error}</p>}
            <div>
              <Label className="label-text text-[var(--text-muted)] mb-2 block">NOWE HASŁO (min. 10 znaków, litery i cyfry)</Label>
              <div className="flex gap-2">
                <Input value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} autoComplete="off"
                  className={`${field} font-mono`} data-testid="client-password-input" />
                <button type="button" onClick={() => setPassword(generatePassword())} aria-label="Wygeneruj hasło" title="Wygeneruj"
                  className="cta-ghost w-12 h-12 rounded-full flex items-center justify-center shrink-0"><RefreshCw className="w-4 h-4" /></button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="cta-primary w-full h-12 rounded-full text-xs font-bold tracking-[0.2em] uppercase disabled:opacity-60"
              data-testid="client-password-submit">
              {busy ? 'Zapisywanie...' : 'Ustaw hasło'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
